import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useRoomController, useRoomState } from "../../room/RoomContext";
import { Field, FieldDescription, FieldLabel } from "../ui/field";
import { VoiceThresholdSlider } from "./VoiceThresholdSlider";

export function VoiceThresholdControl() {
  const { t } = useTranslation();
  const controller = useRoomController();
  const meter = useRef<HTMLSpanElement>(null);
  const position = useRoomState((s) => s.preferences.voiceThresholdPosition);
  const decibels = useRoomState((s) => s.preferences.voiceThresholdDecibels);
  const valueText = `${decibels} dB`;
  useEffect(
    () =>
      controller.subscribeVoiceLevel((snapshot) => {
        if (!meter.current) return;
        meter.current.dataset.active = String(snapshot.active);
        meter.current.dataset.detected = String(snapshot.detected);
        meter.current.style.setProperty("--voice-level", `${snapshot.level}%`);
      }),
    [controller],
  );
  return (
    <Field>
      <div className="flex items-center justify-between gap-3">
        <FieldLabel id="voiceThresholdLabel" htmlFor="voiceThreshold">
          {t("voice.threshold")}
        </FieldLabel>
        <output className="text-sm text-muted-foreground tabular-nums">{valueText}</output>
      </div>
      <VoiceThresholdSlider
        id="voiceThreshold"
        min={0}
        max={100}
        step={1}
        value={[position]}
        onValueChange={(values) => controller.setVoiceThreshold(values[0])}
        aria-labelledby="voiceThresholdLabel"
        aria-valuetext={valueText}
        meter={
          <span
            ref={meter}
            data-active="false"
            data-detected="false"
            className="absolute inset-y-0 left-0 w-(--voice-level) rounded-full bg-muted-foreground transition-colors data-[detected=true]:bg-quality-good"
            aria-hidden="true"
          />
        }
      />
      <FieldDescription className="flex justify-between gap-3">
        <span>{t("voice.moreSensitive")}</span>
        <span>{t("voice.lessSensitive")}</span>
      </FieldDescription>
    </Field>
  );
}
